import {
  postFillStateOf,
  type FillAllocationDto,
  type FillLineDto,
  type FillState,
  type PostAudience,
} from '@pams/shared';
import { isStaffable } from './fillCursor';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §1] A POST, SUMMARISED — the grain the fill screen did not have.
 *
 *  ⚠️ DERIVED ON THE CLIENT, AND THE WIRE IS NOT WIDENED FOR IT.
 *
 *  `FillLineDto` already IS the post: `{lineKey, label, kind, eligibleRanks, displayOrder,
 *  allocations[]}`. Before C1f `FillLines` flattened it — one row per ALLOCATION — so the
 *  post existed in the contract and nowhere on the screen. §1e stops flattening it; nothing
 *  new has to be sent.
 *
 *  ⚠️ AND IT CANNOT COME FROM THE SERVER ANYWAY, because `staged` counts. A "copy from last
 *  time" proposal is a name the operator has put on the row and the server has not been told
 *  about, and §1b treats it as naming — so a server-computed post total would disagree with
 *  the screen for as long as the proposal sits there uncommitted.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [C1f §1h] HOW MANY SLOTS A DELEGATED UNIT LINE SHOWS BEFORE IT COLLAPSES.
 *
 * "Beyond about four", per the brief. A division scanning a post it has split four ways
 * wants the GAP, not thirty avatars.
 *
 * ⚠️ IT IS READ, NEVER RE-TYPED — a literal `4` in the component and a `4` here are two
 * expressions of one number, and the forgotten one fails silently.
 */
export const UNIT_LINE_CHIP_CAP = 4;

export type PostSummary = {
  allocatedCount: number;
  namedCount: number;
  /**
   * ⚠️ [UX1 §3a] SLOTS SITTING ON A ROW THIS ADDRESSEE HANDED DOWN — named or not.
   *
   * It REPLACES [C1f]'s `delegated: boolean`. That flag answered "has anything been split",
   * which is the only question §1b's now-deleted `postIsUnrouted` ever asked; §3a needs the
   * QUANTITY, and keeping both would be two derivations of one fact with the boolean going
   * stale the first time somebody summed the counts differently.
   */
  delegatedCount: number;
  /**
   * ⚠️ Of `namedCount`, the ones standing on such a row. THE DISAMBIGUATOR, AND IT IS NEVER
   * RENDERED — `postFillStateOf` subtracts it so a handed-down slot is not counted twice.
   * See that function; the case it prevents is a green row over two empty retained slots.
   */
  delegatedNamedCount: number;
  fillState: FillState;
};

/**
 * The post's own numbers, and its state.
 *
 * `staged` is the fill screen's uncommitted proposal, keyed by `slotRef`; pass an empty set
 * anywhere it does not apply (the arrangement detail page has none).
 *
 * ⚠️ [UX1 §3c] `audience` IS REQUIRED AND IS NOT DEFAULTED. A default would put one reading in
 * the omitted position and ship the wrong one silently — [P5c.0]'s rule for exactly this
 * family of function, and here it is the mechanism that forced `DutyOfficers.tsx` to declare
 * an answer, which is the file that had no `post-tone` claim at all.
 */
export const postSummary = (line: FillLineDto, staged: ReadonlySet<string>, audience: PostAudience): PostSummary => {
  let allocatedCount = 0;
  let namedCount = 0;
  let delegatedCount = 0;
  let delegatedNamedCount = 0;

  for (const allocation of line.allocations) {
    allocatedCount += allocation.allocatedCount;
    /* ⚠️ THE PARENT KEY, NEVER A BOOLEAN — `fillAllocationSchema`'s own rule. `CD:BOW` can
       ALSO exist because the CP office asked Bowbazar directly, and that row is NOT delegated. */
    const handedDown = allocation.delegatedFromAllocKey !== null;
    if (handedDown) delegatedCount += allocation.allocatedCount;

    for (const slot of allocation.slots) {
      /* ⚠️ A STAGED NAME COUNTS, on BOTH sides of the partition. [C1f]'s rule: what the
         operator has DONE, not what has been saved. */
      if (slot.assignment?.officer || staged.has(slot.slotRef)) {
        namedCount += 1;
        if (handedDown) delegatedNamedCount += 1;
      }
    }
  }

  return {
    allocatedCount,
    namedCount,
    delegatedCount,
    delegatedNamedCount,
    fillState: postFillStateOf({ allocatedCount, namedCount, delegatedCount, delegatedNamedCount, audience }),
  };
};

/**
 * ⚠️ [UX1 §2a] IS THERE ANYTHING LEFT TO ROUTE? — a POST-grain question, and a REVERSAL of
 * [C1f §1b].
 *
 * That pass restricted the control to an EMPTY post — no names AND no delegations — and its
 * own header predicted this change in terms: *"a later 'change the split' affordance is a
 * change to this predicate and to nothing else."* The client has asked for it. `postIsUnrouted`
 * is DELETED rather than loosened, because the fact being asked is a different one.
 *
 * ⚠️ THE SERVER WAS NEVER NARROWED AND IS NOT NARROWED NOW. `getDelegationOptions` has always
 * answered `delegable = max(0, pool - maxNamed(source))` with a per-unit `namedFloor`, where
 * `pool = retained + Σ family` — so the brief's "4 required, 2 delegated, 2 still delegable"
 * has computed correctly since [C1b]. All that changes is whether the UI asks.
 *
 * ⚠️ A STAGED NAME STILL COUNTS, through `namedCount`. Offering to route a post whose chips
 * are already full of a copied proposal reads as the screen having forgotten it.
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2a §5g] CONSERVATION, MADE EXPRESSIBLE ON THE CLIENT FOR THE FIRST TIME.
 *
 *  The server has held `filled(d) + delegated + remaining(d) = pool` since [C1b §3] —
 *  `delegationPlan.util.ts` states it and `delegation.test.ts` CLAIM 1 pins it on every night
 *  of a three-night fixture. Nothing in `web/src` could even STATE it: `postSummary` produces
 *  four numbers and relates none of them, and `POST_COPY.shortfall` computes `total - named`,
 *  which counts handed-down slots as gaps.
 *
 *  ⚠️ THE OBVIOUS ARITHMETIC IS WRONG, AND IT IS WRONG IN THE DANGEROUS DIRECTION.
 *  `namedCount` and `delegatedCount` OVERLAP by `delegatedNamedCount` — a name standing on a
 *  handed-down row is in both — so `named + delegated` DOUBLE-COUNTS. That is [UX1 §3]'s
 *  recorded catastrophe: four asked for, two handed to a station which names both, nothing
 *  named on the two the division kept, and the naive sum reads 4 of 4 over two empty slots.
 *
 *  ⚠️ SO `filled` IS THE ADDRESSEE'S **OWN** NAMES — `namedCount - delegatedNamedCount` — and
 *  that subtraction is the whole of the correctness here. `postFillStateOf` performs the same
 *  one for the same reason, a few lines apart in `fillState.constant.ts`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const postConservation = (
  summary: PostSummary,
): { required: number; filled: number; delegated: number; remaining: number } => {
  const delegated = summary.delegatedCount;
  const retained = summary.allocatedCount - delegated;
  /* ⚠️ The addressee's OWN names. Never `summary.namedCount` — see the header. */
  const filled = summary.namedCount - summary.delegatedNamedCount;
  return { required: summary.allocatedCount, filled, delegated, remaining: retained - filled };
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C4a §6] THE POST HEADING'S NUMBERS — COMPONENTS, NEVER A COMPOSITE.
 *
 *  Asked for three times: [UX1 §3] changed the TONE and left the words at `N of M named`;
 *  [C3a §5c] changed the words to `(2 of 4 delegated · 2 named)`, whose "named" was the WHOLE
 *  `namedCount` — names standing on handed-down rows included — so the two clauses overlapped and
 *  nothing on the screen summed to the post. The instruction kept not landing because ONE number
 *  was being made to serve TWO readers who need different answers:
 *
 *    ADDRESSEE  "what is left of MY work?"   → own names · delegated · to go
 *    OVERSIGHT  "is anybody standing there?" → names · not yet named
 *
 *  ⚠️ THE ADDRESSEE'S THREE PARTS SUM TO THE POST, WHICH IS WHY THEY ARE `postConservation`'s.
 *  `filled` subtracts `delegatedNamedCount` — the whole of that function's correctness — so a
 *  station's names inside a handed-down row are carried by `delegated` and never counted twice.
 *  `to go` reaching zero is exactly `postFillStateOf`'s addressee `NAMED`, so the words and the
 *  green dot cannot disagree.
 *
 *  ⚠️ OVERSIGHT'S `named` IS THE FULL COUNT, deliberately. Every name there is a real officer at
 *  the place, whoever named them — and delegation is not a component of that reading at all
 *  ([UX1 §3b]: "delegation is still not fulfilment"), which is why it has no `delegated` part.
 *
 *  `Math.max(0, …)` is defensive only: a slot is named once, so neither remainder can go below
 *  zero from this data.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type PostHeadingCounts =
  | { audience: 'addressee'; filled: number; delegated: number; remaining: number }
  | { audience: 'oversight'; named: number; unnamed: number };

export const postHeadingCounts = (summary: PostSummary, audience: PostAudience): PostHeadingCounts => {
  if (audience === 'addressee') {
    const { filled, delegated, remaining } = postConservation(summary);
    return { audience: 'addressee', filled, delegated, remaining: Math.max(0, remaining) };
  }
  return {
    audience: 'oversight',
    named: summary.namedCount,
    unnamed: Math.max(0, summary.allocatedCount - summary.namedCount),
  };
};

export const postHasRemainder = (summary: PostSummary): boolean => summary.namedCount < summary.allocatedCount;

/**
 * ⚠️ [UX1 §2b] MAY *THIS ROW* BE SPLIT? — an ALLOCATION-grain question, and a DIFFERENT FACT
 * from `mayDelegateAllocation`, which narrows by the CALLER's org unit type.
 *
 * `CD · E1` is already at a unit: there is nothing beneath it for this operator to decide, so
 * the control is ABSENT rather than disabled — [C1f §1c]'s rule, that a disabled control is a
 * promise something could be done here.
 *
 * ⚠️ IT IS AN *OFFER* GATE AND THE SERVER IS NOT NARROWED. `requireDelegationContext` will
 * still split a unit-addressed source WITHIN itself, one level, and `delegableUnitsUnder`
 * answers `[]` for a leaf. This removes an affordance, never a verb.
 *
 * ⚠️ AND A HANDED-DOWN CHILD NEEDS NO SECOND CLAUSE. `delegationTargetSchema` REQUIRES
 * `unitOrgUnitId`, so every delegated row has a unit and is already excluded here. The
 * one-level chain guard stays where it is enforced, on the server.
 */
export const allocationMayBeSplit = (allocation: FillAllocationDto): boolean => allocation.unit === null;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C3c §5c] HAS THIS ADDRESSEE DISCHARGED THE WHOLE SHEET? — [UX1 §3]'s rule, one level up.
 *
 *  Nothing new is computed. `postFillStateOf` with `audience: 'addressee'` already answers
 *  "named, delegated downward, or a mix of both" for ONE post, with the overlap subtracted so a
 *  handed-down slot is not counted twice; this asks whether that is true of every post asked of
 *  them. The screen already renders the per-post answer, so the footer cannot disagree with the
 *  rows above it.
 *
 *  ⚠️ `audience !== 'addressee'` RETURNS FALSE AT THE TOP, AND THAT IS THE "DO NOT LET IT LEAK
 *  UPWARD" CLAUSE, STATED ONCE. Three independent facts keep it true:
 *
 *    1. `fillAudienceFor` answers `'oversight'` for a COMMISSIONERATE node AND for any
 *       `SUPER_ADMIN` — the second clause is load-bearing, because `scopeForActor` maps that role
 *       to `GLOBAL`, so a super admin posted at a division still reads every division's lines;
 *    2. this is DERIVED ON THE CLIENT and written nowhere, so no aggregate can read it;
 *    3. THE SERVER CANNOT SEE DELEGATION AT ALL. `RANK_EXPRESSION` aggregates `allocation_fills`,
 *       which carries no `delegated_from_alloc_key` — that column lives on the arrangement's own
 *       requirement lines. The inbox, both dashboard fill tiles and the notification bell are
 *       therefore unreachable from here BY CONSTRUCTION rather than by discipline.
 *
 *  ⚠️ AND A UNIT NEEDS NO SPECIAL CASE, WHICH IS WHY `fillAudienceFor` IS NOT WIDENED. A station
 *  has nothing beneath it to delegate to (`mayDelegateAllocation` refuses a `UNIT`), so its
 *  `delegatedCount` is always 0 — and with no delegation the two audiences reduce to the same
 *  arithmetic. "Every position named" already IS "every position discharged" there, through the
 *  ordinary `NAMED` path. Widening the predicate would be a no-op today and a silent behaviour
 *  change the day it stopped being one.
 *
 *  ─── ⚠️ [C6a §4] AND A COMMAND POSITION ASKED OF THE DIVISION IS PART OF ITS PART ────────────
 *
 *  A division asked for an In-charge has not done its part while that position is unnamed, however
 *  full its force posts are. `command` says so as TWO BOOLEANS — `asked` and `outstanding`, derived by
 *  the command-families helper beside this file — and this file never learns what a command family is:
 *  every number above (`postSummary`, the post heading, the footer's `n of m named`) is computed from
 *  force lines alone, and a command position is never a number (FR-5.2.6b).
 *
 *  ⚠️ REQUIRED, NEVER DEFAULTED — [UX1 §3c]/[P5c.0]'s rule for this family of function. A default of
 *  "nothing asked" would put the reading that shows the pill in the omitted position, and a caller who
 *  forgot the command half would call a division done over an unnamed In-charge, silently.
 *
 *  ⚠️ THE COMMAND HALF READS HANDING-ON EXACTLY AS THIS FILE READS DELEGATION: a position the division
 *  HANDED to a unit is the unit's ([UX1 §3]), and one the ORDER asked of a unit directly is still owed,
 *  just as `postSummary` counts a directly-addressed `CD:BOW` row's empty slots against the division.
 *  And a division asked ONLY for command is done once those are named: `asked.length > 0 ||
 *  command.asked`, because "asks nothing of this addressee" is no longer the same as "has no force posts".
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/** What the reading division still owes in command — yes/no only, never how many. */
export type CommandObligation = { asked: boolean; outstanding: boolean };

export const sheetDischarged = (
  lines: readonly FillLineDto[],
  staged: ReadonlySet<string>,
  audience: PostAudience,
  command: CommandObligation,
): boolean => {
  if (audience !== 'addressee') return false;

  /*
   * ⚠️ ONLY POSTS THAT ASK FOR SOMEBODY. `postFillStateOf` answers `NOT_NAMED` for a requirement
   * of zero — "a row cannot read as an achievement before it started" — so a zero-count column
   * would hold the footer shut for ever.
   *
   * ⚠️ [P7A §3] AND ONLY LINES A PERSON CAN STAND IN. A vehicle line is never named, so weighing it
   * held "Your part is done" off every sheet that carried a van, for ever (status-matrix F5, now D23).
   * `isStaffable` is `fillCursor.ts`'s — the one client expression of the rule, which the slot
   * sequence already reads; a vehicles-only sheet therefore asks nothing and is not "done".
   */
  const asked = lines
    .filter(isStaffable)
    .map((line) => postSummary(line, staged, audience))
    .filter((summary) => summary.allocatedCount > 0);

  /* ⚠️ AND A SHEET THAT ASKS NOTHING OF THIS ADDRESSEE IS NOT "DONE"; it is empty. */
  return (
    (asked.length > 0 || command.asked) &&
    asked.every((summary) => summary.fillState === 'NAMED') &&
    !command.outstanding
  );
};
